import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Loader2, Clock, RotateCw, Play, CheckCircle2, Circle, RefreshCw, PartyPopper, X } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import ExerciseCard from "../components/ExerciseCard";
import WorkoutGenerateForm, { EQUIPMENT_OPTIONS } from "../components/WorkoutGenerateForm";
import { haptic } from "../lib/haptics";

/**
 * Compute which program week the user is currently on, derived from the
 * plan's `created_at` timestamp (one week per 7 calendar days, clamped to
 * the plan's `total_weeks`). Returns 1 if anything's missing so we always
 * land somewhere sensible.
 */
const computeCurrentProgramWeek = (createdAt, totalWeeks) => {
  if (!createdAt) return 1;
  const start = new Date(createdAt).getTime();
  if (Number.isNaN(start)) return 1;
  const daysElapsed = Math.floor((Date.now() - start) / 86_400_000);
  const week = Math.floor(daysElapsed / 7) + 1;
  const max = Math.max(1, Number(totalWeeks) || 1);
  return Math.min(Math.max(1, week), max);
};

export default function WorkoutPage() {
  const { user, refreshUser } = useAuth();
  const [plan, setPlan] = useState(null);
  const [planCreatedAt, setPlanCreatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [notes, setNotes] = useState("");
  const [selectedWeek, setSelectedWeek] = useState(1);
  // Flips true once we've auto-landed on the current program week. Prevents
  // us from re-overriding the user's manual tab choice if they switch weeks
  // after we initially load.
  const initialWeekSetRef = useRef(false);
  const [completedByDay, setCompletedByDay] = useState({}); // { "Monday": {...}, "Day 2": {...} } — legacy: any week
  const [completedByWeekAndDay, setCompletedByWeekAndDay] = useState({}); // { "1": { Monday: {...} }, "2": {...}, "_legacy": {...} }
  const [togglingDay, setTogglingDay] = useState(null);

  // onboarding form state
  const [goal, setGoal] = useState(user?.goal || "build_muscle");
  const [level, setLevel] = useState(user?.fitness_level || "intermediate");
  const [days, setDays] = useState(user?.days_per_week || 4);
  const [weeks, setWeeks] = useState(4);
  const [sessionDuration, setSessionDuration] = useState(60);
  const [equipment, setEquipment] = useState(user?.equipment || ["dumbbells", "bodyweight"]);

  const loadThisWeek = useCallback(async () => {
    try {
      const { data } = await api.get("/workouts/sessions/this-week");
      setCompletedByDay(data.by_day || {});
      setCompletedByWeekAndDay(data.by_week_and_day || {});
    } catch (err) {
      console.error("this-week sessions load failed", err);
    }
  }, []);

  useEffect(() => {
    api.get("/workouts/current")
      .then((r) => {
        setPlan(r.data?.plan || null);
        setPlanCreatedAt(r.data?.created_at || null);
      })
      .finally(() => setLoading(false));
    loadThisWeek();
  }, [loadThisWeek]);

  // Auto-land on the current program week the first time the plan loads.
  // After that, the user's tab clicks win — we never re-override.
  useEffect(() => {
    if (initialWeekSetRef.current) return;
    if (!plan?.days || !planCreatedAt) return;
    const current = computeCurrentProgramWeek(planCreatedAt, plan.total_weeks);
    setSelectedWeek(current);
    initialWeekSetRef.current = true;
  }, [plan, planCreatedAt]);

  // Refresh completed-status when the tab regains focus (e.g. after running a session in another tab)
  useEffect(() => {
    const onFocus = () => loadThisWeek();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [loadThisWeek]);

  const markComplete = async (day) => {
    setTogglingDay(day.day);
    try {
      await api.post("/workouts/complete-session", {
        day: day.day,
        focus: day.focus,
        exercises_count: day.exercises?.length || 0,
        program_week: selectedWeek,
      });
      await loadThisWeek();
      haptic.success();
      toast.success(`${day.day} marked complete`);
    } catch (err) {
      haptic.error();
      toast.error("Could not mark complete");
    } finally {
      setTogglingDay(null);
    }
  };

  const unmarkComplete = async (day) => {
    setTogglingDay(day.day);
    try {
      await api.delete(
        `/workouts/sessions/this-week/${encodeURIComponent(day.day)}?program_week=${selectedWeek}`
      );
      await loadThisWeek();
      haptic.tap();
      toast.success(`${day.day} reset`);
    } catch (err) {
      toast.error("Could not undo");
    } finally {
      setTogglingDay(null);
    }
  };

  const toggleEquip = (eq) =>
    setEquipment((cur) => (cur.includes(eq) ? cur.filter((e) => e !== eq) : [...cur, eq]));

  const generate = async () => {
    setGenerating(true);
    try {
      // save profile bits first
      await api.put("/auth/profile", {
        goal,
        fitness_level: level,
        days_per_week: Number(days),
        equipment,
      });
      await refreshUser();
      const { data } = await api.post("/workouts/generate", {
        notes,
        weeks: Number(weeks),
        session_duration_min: Number(sessionDuration),
      });
      setPlan(data.plan);
      setPlanCreatedAt(data.created_at || new Date().toISOString());
      // Newly generated → start on Week 1 regardless of calendar.
      initialWeekSetRef.current = true;
      setSelectedWeek(1);
      toast.success("Your AI workout plan is ready");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate plan");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return <div className="p-10 text-white/60">Loading…</div>;
  }

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto fade-up" data-testid="workout-page">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="label-eyebrow text-[#6B9BFF] mb-2">Workout Plan</div>
          <h1 className="font-display text-4xl sm:text-5xl leading-none">
            {plan ? plan.plan_name || "Your Weekly Plan" : "Build your plan"}
          </h1>
          {plan?.summary && <p className="text-white/60 mt-3 max-w-2xl">{plan.summary}</p>}
        </div>
        {plan && (
          <button
            onClick={() => setPlan(null)}
            className="border border-white/20 hover:border-[#6B9BFF] hover:text-[#6B9BFF] px-5 py-3 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-2"
            data-testid="regenerate-button"
          >
            <RotateCw className="w-4 h-4" /> Regenerate
          </button>
        )}
      </div>

      {!plan && (
        <WorkoutGenerateForm
          goal={goal} setGoal={setGoal}
          level={level} setLevel={setLevel}
          days={days} setDays={setDays}
          weeks={weeks} setWeeks={setWeeks}
          sessionDuration={sessionDuration} setSessionDuration={setSessionDuration}
          equipment={equipment} toggleEquip={toggleEquip}
          notes={notes} setNotes={setNotes}
          generating={generating}
          onGenerate={generate}
        />
      )}

      {plan?.days && (
        <>
          {(() => {
            const trainingDays = plan.days.filter((d) => d.exercises?.length > 0);
            const lastWeek = Math.max(1, Number(plan.total_weeks) || 1);
            const lastWeekMap = completedByWeekAndDay[String(lastWeek)] || {};
            const lastWeekDone = trainingDays.length > 0
              && trainingDays.every((d) => !!lastWeekMap[d.day]);
            // Per-plan dismissal: we use the plan id (or created_at as a stable
            // fallback) so a NEW plan re-shows the banner naturally.
            const dismissKey = `program-complete-dismissed:${plan?.id || planCreatedAt || "current"}`;
            const dismissed = typeof window !== "undefined" && localStorage.getItem(dismissKey) === "1";
            if (!lastWeekDone || dismissed) return null;
            return (
              <div
                className="mb-6 relative overflow-hidden border border-[#6B9BFF]/40 bg-gradient-to-br from-[#6B9BFF]/[0.18] via-[#7C3AED]/[0.10] to-transparent p-6 sm:p-7"
                data-testid="program-complete-banner"
              >
                <button
                  type="button"
                  onClick={() => {
                    localStorage.setItem(dismissKey, "1");
                    // Force a re-render — touching a state used here would
                    // do it; we lean on `setPlan` with the same value to
                    // keep things explicit without a dedicated dismiss flag.
                    setPlan({ ...plan });
                  }}
                  aria-label="Dismiss"
                  className="absolute top-3 right-3 w-8 h-8 inline-flex items-center justify-center text-white/40 hover:text-white border border-white/10 hover:border-white/30 rounded-full transition-colors"
                  data-testid="program-complete-dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
                <div className="flex items-start gap-4 max-w-2xl">
                  <div className="shrink-0 w-12 h-12 rounded-2xl bg-[#6B9BFF] text-black flex items-center justify-center">
                    <PartyPopper className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="label-eyebrow text-[#6B9BFF] mb-1">Program complete</div>
                    <h3 className="font-display text-2xl sm:text-3xl leading-tight">
                      You finished {plan.plan_name || "your program"}.
                    </h3>
                    <p className="text-white/70 text-sm mt-2 leading-relaxed">
                      {plan.total_weeks} week{Number(plan.total_weeks) > 1 ? "s" : ""}, every
                      session ticked off — nice work. Ready for the next chapter? Spin up a fresh
                      program tuned to where you&apos;re at now.
                    </p>
                    <div className="mt-5 flex flex-wrap items-center gap-3">
                      <button
                        type="button"
                        onClick={() => {
                          setPlan(null);
                          initialWeekSetRef.current = false;
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        }}
                        className="bg-[#6B9BFF] text-black font-black uppercase tracking-widest text-xs px-5 py-3 hover:bg-white transition-colors inline-flex items-center gap-2"
                        data-testid="program-complete-cta"
                      >
                        <RotateCw className="w-4 h-4" />
                        Build my next program
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          localStorage.setItem(dismissKey, "1");
                          setPlan({ ...plan });
                        }}
                        className="text-xs uppercase tracking-widest text-white/60 hover:text-white"
                        data-testid="program-complete-keep-training"
                      >
                        Keep training on this plan →
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
          {(() => {
            const trainingDays = plan.days.filter((d) => d.exercises?.length > 0);
            // Completions for the currently-selected program week ONLY.
            const weekMap = completedByWeekAndDay[String(selectedWeek)] || {};
            const isDone = (day) => !!weekMap[day.day];
            const completedThisWeek = trainingDays.filter(isDone).length;
            const totalTraining = trainingDays.length;
            const pct = totalTraining > 0 ? Math.round((completedThisWeek / totalTraining) * 100) : 0;
            return (
              <div
                className="mb-6 border border-white/10 bg-[#121212] p-5 flex items-center justify-between flex-wrap gap-4"
                data-testid="week-progress-card"
              >
                <div>
                  <div className="label-eyebrow text-[#6B9BFF] mb-1">Week {selectedWeek}</div>
                  <div className="font-display text-2xl">
                    <span data-testid="week-progress-completed">{completedThisWeek}</span>
                    <span className="text-white/40"> / {totalTraining} sessions completed</span>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="hidden md:flex flex-wrap gap-1.5" data-testid="week-progress-dots">
                    {trainingDays.map((d) => {
                      const done = isDone(d);
                      return (
                        <span
                          key={d.day}
                          className={`px-2 py-1 text-[10px] uppercase tracking-wider border ${
                            done
                              ? "border-[#6B9BFF] text-[#6B9BFF] bg-[#6B9BFF]/10"
                              : "border-white/10 text-white/40"
                          }`}
                          title={done ? `${d.day} completed` : `${d.day} pending`}
                        >
                          {d.day.slice(0, 3)}
                        </span>
                      );
                    })}
                  </div>
                  <div className="font-display text-3xl text-[#6B9BFF] w-16 text-right" data-testid="week-progress-pct">
                    {pct}%
                  </div>
                </div>
              </div>
            );
          })()}

          {Number(plan.total_weeks) > 1 && (
            <div className="mb-6 border border-white/10 bg-[#121212] p-4" data-testid="week-tabs">
              <div className="label-eyebrow text-white/50 mb-3">Program weeks</div>
              <div className="flex flex-wrap gap-2 mb-4">
                {(() => {
                  const programCurrentWeek = computeCurrentProgramWeek(planCreatedAt, plan.total_weeks);
                  return Array.from({ length: plan.total_weeks }, (_, idx) => idx + 1).map((w) => {
                    const isLiveWeek = w === programCurrentWeek;
                    return (
                      <button
                        key={w}
                        onClick={() => setSelectedWeek(w)}
                        className={`relative px-4 py-2 text-xs uppercase tracking-wider border transition-colors ${
                          selectedWeek === w
                            ? "border-[#6B9BFF] text-[#6B9BFF] bg-[#6B9BFF]/5"
                            : "border-white/10 text-white/60 hover:border-white/30"
                        }`}
                        data-testid={`week-tab-${w}`}
                        aria-current={isLiveWeek ? "true" : undefined}
                      >
                        Week {w}
                        {isLiveWeek && (
                          <span
                            className="ml-2 inline-flex items-center gap-1 align-middle text-[9px] font-bold uppercase tracking-widest text-[#6B9BFF]"
                            data-testid={`week-current-badge-${w}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-[#6B9BFF] animate-pulse" />
                            Now
                          </span>
                        )}
                      </button>
                    );
                  });
                })()}
              </div>
              {(() => {
                const prog = (plan.weekly_progression || []).find((p) => p.week === selectedWeek);
                if (!prog) return null;
                return (
                  <div className="border-t border-white/5 pt-3" data-testid={`week-progression-${selectedWeek}`}>
                    <div className="font-display text-lg text-white">
                      Week {selectedWeek}: {prog.focus}
                    </div>
                    <div className="text-sm text-white/60 mt-1">{prog.intensity_note}</div>
                  </div>
                );
              })()}
            </div>
          )}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {plan.days.map((day, i) => {
              // Completion for the SELECTED program week only (not "any week").
              const weekMap = completedByWeekAndDay[String(selectedWeek)] || {};
              const completion = weekMap[day.day];
              const isCompleted = !!completion;
              const hasExercises = day.exercises?.length > 0;
              const toggling = togglingDay === day.day;
              return (
                <div
                  key={`${selectedWeek}-${day.day}-${i}`}
                  className={`border bg-[#121212] p-6 transition-colors ${
                    isCompleted ? "border-[#6B9BFF]/50" : "border-white/10"
                  }`}
                  data-testid={`day-card-${i}`}
                  data-completed={isCompleted ? "true" : "false"}
                >
                  <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <div className="label-eyebrow text-[#6B9BFF]">{day.day}</div>
                        {isCompleted && (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#6B9BFF] border border-[#6B9BFF]/40 bg-[#6B9BFF]/10 px-2 py-0.5"
                            data-testid={`day-completed-badge-${i}`}
                          >
                            <CheckCircle2 className="w-3 h-3" /> Completed
                          </span>
                        )}
                      </div>
                      <div className={`font-display text-2xl mt-1 ${isCompleted ? "text-white/60" : ""}`}>
                        {day.focus}
                      </div>
                      {isCompleted && (
                        <div className="text-[10px] text-white/40 mt-1">
                          Done {new Date(completion.completed_at).toLocaleString("en-US", {
                            weekday: "short",
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {day.duration_min ? (
                        <div className="flex items-center gap-1 text-xs text-white/50">
                          <Clock className="w-3.5 h-3.5" /> {day.duration_min} min
                        </div>
                      ) : null}
                      {hasExercises && !isCompleted && (
                        <>
                          <button
                            type="button"
                            onClick={() => markComplete(day)}
                            disabled={toggling}
                            className="border border-white/15 hover:border-[#6B9BFF] hover:text-[#6B9BFF] uppercase tracking-wider text-xs px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
                            data-testid={`mark-complete-${i}`}
                            title="Mark this session as completed without opening the focused view"
                          >
                            {toggling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Circle className="w-3.5 h-3.5" />}
                            Mark done
                          </button>
                          <Link
                            to={`/session/${i}`}
                            className="bg-[#6B9BFF] text-black font-bold uppercase tracking-wider text-xs px-3 py-2 hover:bg-white inline-flex items-center gap-1.5"
                            data-testid={`start-session-${i}`}
                          >
                            <Play className="w-3.5 h-3.5" /> Start
                          </Link>
                        </>
                      )}
                      {hasExercises && isCompleted && (
                        <button
                          type="button"
                          onClick={() => unmarkComplete(day)}
                          disabled={toggling}
                          className="text-white/40 hover:text-white uppercase tracking-wider text-xs px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`undo-complete-${i}`}
                          title="Reset completion for this day"
                        >
                          {toggling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                          Undo
                        </button>
                      )}
                    </div>
                  </div>
                  {hasExercises ? (
                    <div className="space-y-2">
                      {day.exercises.map((ex, j) => (
                        <ExerciseCard key={j} exercise={ex} dayIndex={i} exIndex={j} />
                      ))}
                    </div>
                  ) : (
                    <div className="text-white/40 text-sm italic">Rest & recover</div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
